import { FORM_ERROR, SubmissionErrors } from 'final-form';
import { omit } from 'lodash';
import { Link, RouteComponentProps } from 'react-router-dom';
import { FC, useEffect, useState } from 'react';
import { withTranslation, WithTranslation, useTranslation } from 'react-i18next';
import { connect, MapDispatchToPropsFunction } from 'react-redux';
import { AxiosResponse } from 'axios';

import { partialUserCoreToPatch, UserCorePatch, UserSettingsAttrs } from '~src/interfaces/user.interface';
import logger from '~src/services/logger/LlsLogger';
import { defaultFeatures, Features } from '~src/interfaces/company.interface';
import TextBtnPageFooter from '~src/shared/footers/textBtnPageFooter/TextBtnPageFooter';
import { MainContainerAnimation } from '~src/styles/animations';
import { fetchAttributesList } from '~src/modules/attributes/actions/attributes.actions';
import ThreeColsHeader from '~src/shared/headers/threeColsHeader/ThreeColsHeader';
import NavArrowLnk from '~src/shared/buttons/NavArrowLnk';
import {
  getUserDetails,
  patchSessionUser,
  updateSessionUserAttrs,
  fetchTimeZones,
  fetchLocales,
} from '~src/store/session/actions';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { routes } from '~src/routingConfig';
import { StoreI } from '~src/rootReducer';
import { ApiReduxResp, ThunkAnyAction } from '~src/services/apiMiddleware';
import { SessionUserI, SystemLocalesI, ContentLocalesI } from '~src/interfaces/session.types';
import { AttributeI } from '~src/interfaces/attributes.types';

import ChangePassword from './components/ChangePassword';
import UserSettingsForm, { FormValuesI } from './components/UserSettingsForm';
import { PageContentContainerExt, PlaceHolder } from './userSettings.sc';
import NotificationsSettings from './components/NotificationsSettings';
import {objectDiff} from "~src/utils/objectDiff";

interface StateProps {
  systemLocales: SystemLocalesI;
  contentLocales: ContentLocalesI;
  features: Partial<Features>;
}

interface DispatchProps {
  fetchLocales(): ApiReduxResp;
  fetchTimeZones(): ApiReduxResp;
  fetchAttributesList(): ApiReduxResp<AttributeI[]>;
  updateAttributes(userId: string, attrs: UserSettingsAttrs): ApiReduxResp;
  updateUser(userId: string, values: UserCorePatch): ApiReduxResp;
  getUserDetails(): ApiReduxResp<SessionUserI>;
}

interface DefaultProps {
  isAuthenticated: boolean;
}

interface OwnProps extends Partial<DefaultProps> {}

type Props = OwnProps & StateProps & DispatchProps & WithTranslation & RouteComponentProps<any>;

const UserSettings: FC<Props> = props => {
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    if (props.isAuthenticated) {
      fetchingStaticAttrs();
      fetchUserDetails();
    }
  }, [props.isAuthenticated]);

  const fetchingStaticAttrs = () => {
    props.fetchAttributesList();
    props.fetchTimeZones();
    props.fetchLocales();
  };

  const fetchUserDetails = async () => {
    setLoadingDetails(true);
    await props.getUserDetails();
    setLoadingDetails(false);
  };

  const [t] = useTranslation();

  const onSubmitSave = (
    values: FormValuesI,
    sessionUser: SessionUserI,
    initialValues: Partial<FormValuesI>,
  ) => {
    const diffVal: Partial<FormValuesI> = objectDiff(
      partialUserCoreToPatch(initialValues),
      partialUserCoreToPatch(values),
    );
    const restCoreValues: UserCorePatch = omit(diffVal, 'attributes');
    const diffAttrs = objectDiff(initialValues.attributes || {}, values.attributes);
    const updateUserPromise = updateUser(sessionUser.uuid, restCoreValues);
    const updateAttributesPromise = updateUserAttributes(sessionUser.uuid, diffAttrs);
    return Promise.all([updateUserPromise, updateAttributesPromise])
      .then(() => {
        fetchUserDetails(); // intentionally ignore. UserSettingsForm will update via state change.
      })
      .catch(error => {
        const errorsArr: SubmissionErrors = { [FORM_ERROR]: t('x.users.save_error') };
        Object.keys(error.response.data).forEach(key => {
          [errorsArr[key]] = error.response.data[key];
        });
        return errorsArr;
      });
  };

  const updateUser = (
    userId: string,
    values: UserCorePatch,
  ): Promise<{ response: Partial<AxiosResponse> } | ThunkAnyAction> =>
    new Promise((resolve, reject) => {
      if (Object.keys(values).length === 0) {
        resolve({ response: { data: { uuid: userId } } });
        return;
      }
      props.updateUser(userId, values).then(response => {
        if (response.error) {
          reject(response.error);
          return;
        }
        resolve(response);
      });
    });

  const updateUserAttributes = (
    userId: string,
    attributes: UserSettingsAttrs,
  ): Promise<{} | ThunkAnyAction> =>
    new Promise((resolve, reject) => {
      if (Object.keys(attributes).length === 0) {
        resolve({});
        return;
      }
      props.updateAttributes(userId, attributes).then(response => {
        if (response.error) {
          logger.info('Error updating User attributes', {
            error: new Error('Error on updating user'),
            info: {
              componentStack: 'ProfileSettings.jsx',
            },
          });
          reject(response.error);
        }
        resolve(response);
      });
    });

  return (
    <PageContentContainerExt>
      <ThreeColsHeader
        title={{
          key: 'x.profile_settings.header.title',
          txt: t('x.profile_settings.header.title'),
        }}
        supTitle={{
          key: 'x.profile_settings.header.subtitle',
          txt: t('x.profile_settings.header.subtitle'),
        }}
        rightColChildren={
          props.features.survey ? (
            <Link to={routes.reports.sessionUser}>
              <NavArrowLnk arrowDir="right" arrOnRight>
                <span data-translation="x.profile_settings.header.linktitle">
                  {t('x.profile_settings.header.linktitle')}
                </span>
              </NavArrowLnk>
            </Link>
          ) : undefined
        }
      />
      {loadingDetails ? (
        <LoadingSpinner marginTop={60} />
      ) : (
        <MainContainerAnimation>
          <UserSettingsForm
            systemLocales={props.systemLocales}
            contentLocales={props.contentLocales}
            onFrmSubmit={onSubmitSave}
          />
        </MainContainerAnimation>
      )}
      {
        <MainContainerAnimation>
          <NotificationsSettings />
        </MainContainerAnimation>
      }
      {
        <MainContainerAnimation>
          <ChangePassword />
        </MainContainerAnimation>
      }
      {props.features.survey ? (
        <TextBtnPageFooter
          t={t}
          infoKey="x.profile_settings.footer.title"
          btnKey="x.profile_settings.footer.button"
          linkUrl={routes.reports.sessionUser}
        />
      ) : (
        <PlaceHolder />
      )}
    </PageContentContainerExt>
  );
};

const mapState2Props = (state: StoreI): StateProps => {
  const { session } = state.user;
  return {
    contentLocales: session.contentLocales,
    systemLocales: session.systemLocales,
    features: session.company ? session.company.features : defaultFeatures,
  };
};

const mapDispatch2Props:MapDispatchToPropsFunction<DispatchProps, OwnProps> = (dispatch): DispatchProps => ({
  fetchTimeZones: () => dispatch(fetchTimeZones()),
  fetchLocales: () => dispatch(fetchLocales()),
  fetchAttributesList: () => dispatch(fetchAttributesList()),
  updateAttributes: (userId, value) => dispatch(updateSessionUserAttrs(userId, value)),
  updateUser: (userId, values) => dispatch(patchSessionUser(userId, values)),
  getUserDetails: () => dispatch(getUserDetails()),
});

const UserSettingsT = withTranslation()(UserSettings);
const UserSettingsC = connect(mapState2Props, mapDispatch2Props)(UserSettingsT);

export default UserSettingsC;
